import { useState, useEffect } from 'react';

import { Input, Select, Button, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@/assets/AddIcon';
import ModalDelete from '@/components/Modals/ModalDelete';
import TableInvoiceShipOwner from '@/components/Tables/TableInvoiceShipOwner';
import TableInvoiceShipOwnerSuperAdmin from '@/components/Tables/TableInvoiceShipOwnerSuperAdmin';
import ModalPaymentConfirmation from '@/components/Modals/ModalPaymentConfirmation';
import TableInvoiceShipOwnerAdminFinance from '@/components/Tables/TableInvoiceShipOwnerAdminFinance';
import { ALLOWED_CHARACTERS_REGEX, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS, INVOICE_STATUS, ROLES } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewInspectionProcess, setDataTotalNewInvoiceInspectingCompany, setDataTotalNewInvoiceShipOperator, setDataTotalNewNominationShipOperator } from '@/redux/slices/notificationSlice';
import { downloadFile } from '@/services/downloadService';
import { deleteInvoicesShipOwner, getListInvoicesByFinance, getListInvoicesByShipOwner, getListInvoicesShipOwner, updateInvoiceStatusShipOwner, updatePaymentStatusShipOwner } from '@/services/invoicesShipOwnerService';
import { getNewRecordCounterAdminFinance, getNewRecordCounterShipOperator, getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';

import '../../styles/ListPage.css';
import ModalShipOperatorInvoiceConfirm, { ShipOperatorInvoiceDataConfirmation } from '@/components/Modals/ModalShipOperatorInvoiceConfirm';
import { getDataRef } from '@/services/refDataService';

const InvoicesShipOwnerList = () => {
    const navigate = useNavigate();
    const { isOpen: isOpenDelete, onOpen, onClose: onCloseDelete } = useDisclosure();
    const { isOpen: isOpenConfirmPayment, onOpen: onOpenConfirmPayment, onClose: onCloseConfirmPayment } = useDisclosure();
    const { isOpen: isOpenConfirmInvoice, onOpen: onOpenConfirmInvoice, onClose: onCloseConfirmInvoice } = useDisclosure();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const shipOwnerId = useAppSelector((state: RootState) => state?.account?.appId);
    const name = useAppSelector((state: RootState) => state.account.name);
    const toast = useToast();
    const dispatch = useAppDispatch();

    const [searchValue, setSearchValue] = useState('');
    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [dataList, setDataList] = useState<any[]>([]);
    const [id, setId] = useState('');
    const [invoiceStatus, setInvoiceStatus] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('');
    // const [invoiceDate, setInvoiceDate] = useState(null) as any;
    const [approvalStatus, setApprovalStatus] = useState('-1');
    const [approvalNote, setApprovalNote] = useState('');
    const [amountIDR, setAmountIDR] = useState(0);
    const [paymentDate, setPaymentDate] = useState<string>('');
    const [dataConfirmation, setDataConfirmation] = useState<any>(null);
    const [selectedDataConfirmInvoice, setSelectedDataConfirmInvoice] = useState<ShipOperatorInvoiceDataConfirmation | undefined>(undefined);
    const [idConfirmation, setIdConfirmation] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessageApproval, setErrorMessageApproval] = useState('');
    const [errorMessageNote, setErrorMessageNote] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [market, setMarket] = useState('') as any;
    const [listMarket, setListMarket] = useState([]) as any;
    const [isPaymentConfirmViewOnly, setIsPaymentConfirmViewOnly] = useState(false);

    const handleNavAdd = () => {
        navigate('/manage-invoices/ship-operator/add');
    }

    const handleChangeSearch = (value: string) => {
        setSearchValue(value);
    }

    const handleSubmitSearch = () => {
        getData();
    }

    const handleEdit = (idEdit: string) => {
        navigate('/manage-invoices/ship-operator/edit', { state: { id: idEdit } });
    }

    const handleDelete = (id: string) => {
        setId(id);
        onOpen();
    }

    const handleConfirmDelete = async () => {
        const obj = {
            invoiceId: id,
            modifiedBy: name || "System"
        }

        const response = await deleteInvoicesShipOwner(obj) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Invoice deleted.',
                description: "We've deleted invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invoice failed to delete.',
                description: "We failed to delete invoice for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onCloseDelete();
    }

    const handlePay = (idPay: string) => {
        if (role === 'super admin') {
            navigate('/manage-invoices/ship-operator/payment', { state: { id: idPay, type: 'ship owner' } });
        } else {
            navigate('/manage-invoices-ship-operator/payment', { state: { id: idPay, type: 'ship owner' } });
        }
    }

    const handleCancelConfirmPayment = () => {
        setApprovalStatus('');
        onCloseConfirmPayment();
    }

    // Accept status and manualPaymentDate from ModalPaymentConfirmation
    const handleConfirmPayment = async (status: string, manualPaymentDate?: string) => {
        if (status === '-1'){
            setErrorMessageApproval('Please choose either approve or reject.');
            return;
        }
        
        if (status === '3' && approvalNote.trim() === '') {
            setErrorMessageNote('Note is required');
            return;
        }

        if (status === '4' && amountIDR <= 0) {
            toast({
                title: 'Amount in IDR is required',
                description: '',
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
            return;
        }

        if (errorMessageNote) {
            return;
        }

        const data = new FormData();
        data.append('invoiceId', idConfirmation);
        data.append('paymentStatus', status);
        data.append('paymentDate', manualPaymentDate || paymentDate);
        data.append('note', approvalNote);
        data.append('totalAmountIDR', String(amountIDR));
        data.append('modifiedBy', name || "System");

        const response = await updatePaymentStatusShipOwner(data) as any;
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Payment status confirmed.',
                description: "We've confirmed the payment.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Payment failed to confirmed.',
                description: "We failed to confirm the payment.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onCloseConfirmPayment();
    }

    const handlePaymentReceived = async (idPay: string) => {
        navigate('/manage-invoices/ship-operator/payment', { state: { id: idPay, type: 'ship owner' } });
    }

    const handlePayAdmin = async (idPay: string) => {
        setApprovalStatus('-1');
        setErrorMessageApproval('');
        setErrorMessageNote('');
        setIdConfirmation(idPay);
        setIsPaymentConfirmViewOnly(false);
        const data = dataList.find((x: any) => x.id === idPay);
        setDataConfirmation(data);
        setAmountIDR(0);
        onOpenConfirmPayment();
    }

    const handlePayAdminViewOnly = async (idPay: string) => {
        setApprovalStatus('-1');
        setErrorMessageApproval('');
        setErrorMessageNote('');
        setIdConfirmation(idPay);
        setIsPaymentConfirmViewOnly(true);
        const data = dataList.find((x: any) => x.id === idPay);
        setDataConfirmation(data);
        setAmountIDR(0);
        onOpenConfirmPayment();
    }

    const handleReview = (id: string) => {
        navigate('/manage-invoices-ship-operator/review', { state: { id } })
    }

    const handleChangeApprovalStatus = (value: string) => {
        if (value !== '-1'){
            setErrorMessageApproval('');
        }
        setApprovalStatus(value);
    }

    const handleChangeNote = (value: string) => {
        setErrorMessageNote('');

        if (value) {
            if (value.length > 50) {
                setErrorMessageNote('Max length is 50 characters');
            }

            const result = ALLOWED_CHARACTERS_REGEX.test(value);
            if (!result) {
                setErrorMessageNote('Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.');
            }
        } else {
            setErrorMessageNote('Note is required');
        }

        setApprovalNote(value);
    }

    const handleDownload = async (idFile: string) => {
        await downloadFile(idFile);
    }

    const setLatestCounterAdmin = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.invoiceShipOperatorAdminNewDraft || 0));
        }
    }

    const setLatestCounterAdminFinance = async () => {
        const responseCounter = await getNewRecordCounterAdminFinance() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceInspectingCompany(responseCounter?.data?.inspectingCompany || 0));
            dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.shipOperator || 0));
        }
    }

    const setLatestCounterShipOperator = async () => {
        const responseCounter = await getNewRecordCounterShipOperator(shipOwnerId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInvoiceShipOperator(responseCounter?.data?.invoices || 0));
            dispatch(setDataTotalNewNominationShipOperator(responseCounter?.data?.nominationShipOperator || 0));
            dispatch(setDataTotalNewInspectionProcess(responseCounter?.data?.inspectionProcess || 0));
        }
    }

    const getData = async () => {
        const param = {
            search: searchValue,
            pageNumber: currentPageNumber === 0 ? 1 : currentPageNumber,
            pageSize: currentPageSize,
            invoiceStatus: invoiceStatus !== '' ? Number(invoiceStatus) : '',
            paymentStatus,
            type: typeValue,
            market
        };

        let response = {} as any;
        if (role === 'super admin') {
            response = await getListInvoicesShipOwner(param) as any;
            setLatestCounterAdmin();
        } else if (role === 'admin finance') {
            response = await getListInvoicesByFinance(param) as any;
            setLatestCounterAdminFinance();
        } else if (role === 'ship operator') {
            const newParam = { ...param, id: shipOwnerId }
            response = await getListInvoicesByShipOwner(newParam) as any;
            setLatestCounterShipOperator();
        }
        if (response.status === HttpStatusCode.Ok) {
            setDataList(response.data);
            setTotalPageNumber(response.totalPage);
            setCurrentPageNumber(response.pageNumber);
            setHasNextPage(response.hasNextPage);
        } else {
            toast({
                title: response?.error?.code,
                description: response?.error?.message,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        setIsLoading(false);
    }

    const handleOpenConfirmInvoice = (idView: string) => {
        //setSelectedSequenceId(sequenceID);
        setApprovalStatus('-1');
        setErrorMessageApproval('');
        setErrorMessageNote('');
        setIdConfirmation(idView);
        const data = dataList.find((x: any) => x.id === idView);
        if (data) {
            setSelectedDataConfirmInvoice({
                invoiceId: idView,
                invoiceNumber: data.invoiceID,
                vesselName: data.shipName,
                totalAmount: data.amount,
                ...data
            } as ShipOperatorInvoiceDataConfirmation);
        }

        onOpenConfirmInvoice();
    }

    const handleCancelInvoiceConfirmation = () => {
        setApprovalStatus('');
        onCloseConfirmInvoice();
    }

    const handleConfirmInvoice = async () => {
        if (approvalStatus === '-1'){
            setErrorMessageApproval('Please choose either approve or reject.');
            return;
        }

        if (approvalStatus === '3' && approvalNote.trim() === '') {
            setErrorMessageNote('Note is required');
            return;
        }

        if (errorMessageNote) {
            return;
        }

        const obj = {
            invoiceId: idConfirmation,
            invoiceStatus: Number(approvalStatus),
            notes: approvalNote,
            modifiedBy: name || "System"
        }

        const response = await updateInvoiceStatusShipOwner(obj);
        console.log(response)
        if (response.status === HttpStatusCode.Ok) {
            getData();
            toast({
                title: 'Invoice status updated.',
                description: "We've updated invoice status for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            toast({
                title: 'Invoice failed to update.',
                description: "We failed to update invoice status for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }

        onCloseConfirmInvoice();
    }

    const getListRef = async (type: string) => {
        const response = await getDataRef(type) as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedData = response.data.map((item: any) => ({
                value: item.value,
                label: item.name,
            }));

            if (type === 'market') {
                setListMarket(formattedData);
            }
        } else {
            setListMarket([]);
        }
    }

    useEffect(() => {
        getListRef('market');
        getData();
    }, []);

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalPaymentConfirmation
                isOpen={isOpenConfirmPayment}
                onClose={onCloseConfirmPayment}
                handleConfirmToCancel={handleCancelConfirmPayment}
                handleConfirm={handleConfirmPayment}
                dataConfirmation={dataConfirmation}
                handleChangeNote={handleChangeNote}
                approvalStatus={approvalStatus}
                handleChangeApprovalStatus={handleChangeApprovalStatus}
                handleDownload={handleDownload}
                errorMessageApproval={errorMessageApproval}
                errorMessageNote={errorMessageNote}
                setPaymentDate={setPaymentDate}
                amountIDR={amountIDR}
                setAmountIDR={setAmountIDR}
                isPaymentConfirmViewOnly={isPaymentConfirmViewOnly}
            />
            <ModalShipOperatorInvoiceConfirm
                isOpen={isOpenConfirmInvoice}
                onClose={onCloseConfirmInvoice}
                handleCancel={handleCancelInvoiceConfirmation}
                handleConfirm={handleConfirmInvoice}
                dataConfirmation={selectedDataConfirmInvoice}
                approvalStatus={approvalStatus}
                handleChangeApprovalStatus={handleChangeApprovalStatus}
                handleChangeNote={handleChangeNote}
                errorMessageApproval={errorMessageApproval}
                errorMessageNote={errorMessageNote}
            />
            <ModalDelete title="Delete it?" isOpen={isOpenDelete} onClose={onCloseDelete} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div className="table-filter-input filter-input-width-10">
                                    <Input placeholder='Search' onChange={(e: any) => handleChangeSearch(e.target.value)} />
                                </div>
                                {role === ROLES.SUPER_ADMIN && <div className="table-filter-input filter-input-width-8">
                                    <Select onChange={(e: any) => setInvoiceStatus(e.target.value)}>
                                        <option key="all" value={''}>All Invoice Status</option>
                                        <option key="draft" value={INVOICE_STATUS.DRAFT}>Draft</option>
                                        <option key="sent" value={INVOICE_STATUS.APPROVED}>Sent</option>
                                        <option key="finance" value={INVOICE_STATUS.FINANCE}>Finance</option>
                                        {/*<option key="sent" value={INVOICE_STATUS.IN_PROGRESS}>Sent</option>
                                        <option key="approved" value={INVOICE_STATUS.APPROVED}>Approved</option>
                                        <option key="revision" value={INVOICE_STATUS.REJECTED}>In Revision</option>*/}
                                    </Select>
                                </div>}
                                <div className="table-filter-input filter-input-width-5">
                                    <Select
                                        placeholder='All Type'
                                        value={typeValue}
                                        onChange={(e) => setTypeValue(e.target.value)}
                                    >
                                        <option value="sire">SIRE</option>
                                        <option value="vetting">Vetting</option>
                                    </Select>
                                </div>
                                <div className="table-filter-input filter-input-width-6">
                                    <Select onChange={(e: any) => setMarket(e.target.value)}>
                                        <option key="all" value=''>All Market</option>
                                        {listMarket.map((item: any) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="table-filter-input filter-input-width-8">
                                    <Select onChange={(e: any) => setPaymentStatus(e.target.value)}>
                                        <option key="all" value=''>All Payment Status</option>
                                        <option key="unpaid" value={0}>Unpaid</option>
                                        <option key="approved" value={1}>In Progress</option>
                                        <option key="paid" value={4}>Paid</option>
                                        <option key="reject" value={3}>Rejected</option>
                                    </Select>
                                </div>
                                <div>
                                    <Button className='table-search-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleSubmitSearch}>
                                        Search
                                    </Button>
                                </div>
                            </div>
                        </div>
                    <div className="table-filter-separator"/>
                    {(role === ROLES.SUPER_ADMIN) &&
                        <Button leftIcon={<AddIcon />} className='table-add-button' bgColor={COLORS.BUTTON.BACKGROUND} color={COLORS.BUTTON.FOREGROUND} variant='solid' borderRadius={10} onClick={handleNavAdd}>
                            Create Invoice
                        </Button>
                    }
                    </div>
                </div>
                {[ROLES.SHIP_OPERATOR].includes(role) && <TableInvoiceShipOwner dataList={dataList} isLoading={isLoading} handlePay={handlePay} handleConfirm={handleOpenConfirmInvoice} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} handleDownload={handleDownload} />}
                {[ROLES.SUPER_ADMIN].includes(role) && <TableInvoiceShipOwnerSuperAdmin dataList={dataList} isLoading={isLoading} handlePay={handlePayAdmin} handleEdit={handleEdit} handleDelete={handleDelete} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} handleDownload={handleDownload} />}
                {[ROLES.FINANCE].includes(role) && <TableInvoiceShipOwnerAdminFinance dataList={dataList} isLoading={isLoading} handlePaymentReceived={handlePaymentReceived} handlePay={handlePayAdmin} handlePayAdminViewOnly={handlePayAdminViewOnly}handleEdit={handleEdit} handleDelete={handleDelete} handleChangePageSize={setCurrentPageSize} handleChangePageNumber={setCurrentPageNumber} currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} hasNextPage={hasNextPage} handleDownload={handleDownload} handleReview={handleReview} />}
            </div>
        </>
    )
}

export default InvoicesShipOwnerList;
